import { isPlatformEmbedded } from '@/lib/platformEmbed';
import { Modal } from '@heroui/react';
import { useCoarsePointer } from '@/lib/useCoarsePointer';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDismissable } from '@/lib/useDismissable';
import { QUICK_REACTIONS } from '@/lib/permissions';
import { Portal } from '@/components/Portal';
import {
  AlarmClockIcon,
  ChatBubbleIcon,
  CopyIcon,
  EditIcon,
  EyeOffIcon,
  FlagIcon,
  FolderIcon,
  ForwardIcon,
  LinkIcon,
  PinIcon,
  ReplyIcon,
  TrashIcon,
  VaultIcon,
} from '@/components/icons';

/**
 * Right-click context menu for a message. Position is fixed-coords from
 * the click event; we render through Portal so the menu can escape any
 * overflow-hidden ancestor. Auto-flips when the click happens near the
 * viewport edge so the menu stays fully visible.
 *
 * Actions are passed in from MessageRow, which owns the message data and
 * the permission gating (mine vs not mine). The menu itself is a dumb
 * positioned list.
 */
export function MessageContextMenu(props: {
  x: number;
  y: number;
  mine: boolean;
  pinned: boolean;
  /** Is this message currently in the user's saved-messages list?
   *  Flips the menu copy between "Сохранить" and "Убрать из сохранённого". */
  saved: boolean;
  onClose: () => void;
  onReply: () => void;
  onTogglePin: () => void | Promise<void>;
  onToggleSave: () => void;
  /** "На полку" — file this saved vault message onto shelves. Only provided
   *  inside the personal Сейф; the item is hidden everywhere else. */
  onAddToShelf?: () => void;
  onRemind: () => void;
  onForward: () => void;
  showThread: boolean;
  /** Pins are a guild-channel feature only — the server rejects pinning in DMs,
   *  so the item is hidden there (parity with mobile). */
  showPin: boolean;
  onOpenThread: () => void;
  onEdit: () => void;
  /** Delete for everyone (own messages / moderators). */
  onDelete: () => void | Promise<void>;
  /** Hide just from me (any message). */
  onDeleteForMe: () => void | Promise<void>;
  onCopyText: () => void | Promise<void>;
  onCopyLink: () => void | Promise<void>;
  onReact: (emoji: string) => Promise<void>;
  /** Report this message. Shown only on foreign messages (you can't report
   *  your own). When absent the item is hidden. */
  onReport?: () => void;
  /** May delete other people's messages (MANAGE_MESSAGES). Enables the
   *  "Удалить сообщение" item on foreign messages. Block/unblock now lives
   *  in the user card (click the avatar/name), not here. */
  canModerate?: boolean;
}) {
  const { t } = useTranslation();
  const sheet = useCoarsePointer();
  const closeRef = useRef(props.onClose);
  closeRef.current = props.onClose;
  const ref = useRef<HTMLDivElement | null>(null);
  // Start the menu *off-screen* so we never paint a wrong position.
  // useLayoutEffect runs synchronously after DOM mutation but BEFORE
  // the browser paints — it measures the real size and writes the
  // corrected pos, so the user only ever sees the menu in its final
  // place. Previously `useEffect` ran after paint, so a click near the
  // right edge briefly painted the menu clipped past viewport before
  // the flip kicked in.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    if (!pos || sheet) return;
    const previous = document.activeElement;
    const menu = ref.current;
    menu?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
    return () => {
      if ((document.activeElement === document.body || menu?.contains(document.activeElement))
        && previous instanceof HTMLElement && previous.isConnected) previous.focus({ preventScroll: true });
    };
  }, [pos, sheet]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (sheet || !el) return;
    const r = el.getBoundingClientRect();
    const margin = 8;
    let nx = props.x;
    let ny = props.y;
    // Flip toward the cursor if the natural anchor would overflow.
    if (nx + r.width + margin > window.innerWidth) nx = props.x - r.width;
    if (ny + r.height + margin > window.innerHeight) ny = props.y - r.height;
    // Final clamp to viewport — handles the pathological case where the
    // menu is wider than the viewport itself (mobile portrait).
    nx = Math.min(Math.max(margin, nx), window.innerWidth - r.width - margin);
    ny = Math.min(Math.max(margin, ny), window.innerHeight - r.height - margin);
    setPos({ x: nx, y: ny });
  }, [props.x, props.y, sheet]);

  // Dismiss via the shared hook (click-outside + Esc + scroll).
  //
  // We used to also register a document-level `contextmenu` listener
  // here to handle "right-click on another bubble closes this menu" —
  // but in React 18 the listener fires for the SAME event that just
  // opened the menu (React commits during the event's bubble phase, the
  // useEffect runs before the event finishes bubbling, and the now-
  // registered listener catches the still-in-flight event on its way
  // up to document). Net effect: menu opened and immediately closed
  // itself within the same millisecond. The other-bubble case is
  // already covered by useDismissable's mousedown listener, so dropping
  // the extra contextmenu listener fixes the close-on-open bug without
  // regressing anything.
  useDismissable(!sheet, ref, props.onClose);

  // Keep one history entry across StrictMode's setup/cleanup replay. A queued
  // cleanup is cancelled by the immediate second setup, avoiding a stale back
  // event that would dismiss a newly opened sheet.
  const [historyMarker] = useState(() => crypto.randomUUID());
  const historyMounted = useRef(false);
  useEffect(() => {
    // Inside the platform's Chat frame Bunker adds no history entries
    // (platformEmbed), so a marker here would turn the closing back() into a
    // navigation of the host page.
    if (!sheet || isPlatformEmbedded()) return;
    historyMounted.current = true;
    if (window.history.state?.bunkerMessageSheet !== historyMarker) {
      window.history.pushState({ ...window.history.state, bunkerMessageSheet: historyMarker }, '');
    }
    const onBack = () => {
      if (window.history.state?.bunkerMessageSheet !== historyMarker) closeRef.current();
    };
    window.addEventListener('popstate', onBack);
    return () => {
      historyMounted.current = false;
      window.removeEventListener('popstate', onBack);
      queueMicrotask(() => {
        if (!historyMounted.current && window.history.state?.bunkerMessageSheet === historyMarker) window.history.back();
      });
    };
  }, [sheet, historyMarker]);

  const body = (
      <div
        ref={ref}
        // Until useLayoutEffect has measured + corrected, render
        // off-screen + invisible. First paint is therefore never at
        // the wrong x — no flash, no clipped overflow on right-edge
        // right-clicks.
        style={
          sheet ? undefined : pos
            ? { left: pos.x, top: pos.y }
            : { left: -9999, top: -9999, visibility: 'hidden' }
        }
        className={sheet ? "studio-message-sheet flex flex-col gap-2" : "fixed z-[60] flex flex-col gap-1.5"}
        role="menu"
        aria-label={t('messages.actions')}
        onKeyDown={(event) => {
          const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
          const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
          let next: number | undefined;
          if (event.key === 'ArrowDown') next = (index + 1) % buttons.length;
          if (event.key === 'ArrowUp') next = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = buttons.length - 1;
          if (next !== undefined) { event.preventDefault(); buttons[next]?.focus(); }
          if (event.key === 'Tab' && !sheet) props.onClose();
        }}
      >
        {/* Quick-react row sits ABOVE the menu — Telegram pattern.
            Pill of 6 emoji icons, click → react + close menu. */}
        <div className="bg-surface flex items-center gap-0.5 self-start rounded-full p-1 shadow-card">
          {QUICK_REACTIONS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={async () => {
                props.onClose();
                await props.onReact(emoji);
              }}
              className="chat-emoji-action grid h-8 w-8 place-items-center rounded-lg text-[16px]"
              title={t('messages.reactionWith', { emoji })}
            >
              <span aria-hidden>{emoji}</span>
            </button>
          ))}
        </div>
        {/* One quiet panel: the most used actions first, then keep/share,
            then the destructive tail with only «Удалить у всех» in red. No
            section captions — three short groups read at a glance, and the
            tiny grey labels were noise. shadow-card: the skin zeroes
            --separator, so a floating panel needs the lift to have an edge. */}
        <div className="bg-surface min-w-[224px] overflow-hidden rounded-xl p-1 shadow-card">
          <CtxItem icon={<ReplyIcon className="h-4 w-4" />} onClick={props.onReply}>
            {t('messages.reply')}
          </CtxItem>
          <CtxItem icon={<CopyIcon className="h-4 w-4" />} onClick={props.onCopyText}>
            {t('messages.copyText')}
          </CtxItem>
          <CtxItem icon={<ForwardIcon className="h-4 w-4" />} onClick={props.onForward}>
            {t('messages.forward')}
          </CtxItem>
          {props.showThread && (
            <CtxItem icon={<ChatBubbleIcon className="h-4 w-4" />} onClick={props.onOpenThread}>
              {t('messages.thread')}
            </CtxItem>
          )}
          <CtxDivider />
          <CtxItem icon={<VaultIcon className="h-4 w-4" />} onClick={props.onToggleSave}>
            {props.saved ? t('messages.unsave') : t('messages.save')}
          </CtxItem>
          {props.onAddToShelf && (
            <CtxItem icon={<FolderIcon className="h-4 w-4" />} onClick={props.onAddToShelf}>
              {t('shelves.addAction')}
            </CtxItem>
          )}
          <CtxItem icon={<AlarmClockIcon className="h-4 w-4" />} onClick={props.onRemind}>
            {t('messages.remind')}
          </CtxItem>
          {props.showPin && (
            <CtxItem icon={<PinIcon className="h-4 w-4" />} onClick={props.onTogglePin}>
              {props.pinned ? t('messages.unpin') : t('messages.pin')}
            </CtxItem>
          )}
          <CtxItem icon={<LinkIcon className="h-4 w-4" />} onClick={props.onCopyLink}>
            {t('messages.copyLink')}
          </CtxItem>
          <CtxDivider />
          {props.mine && (
            <CtxItem icon={<EditIcon className="h-4 w-4" />} onClick={props.onEdit}>
              {t('messages.edit')}
            </CtxItem>
          )}
          {/* Report — foreign messages only (can't report your own). */}
          {!props.mine && props.onReport && (
            <CtxItem icon={<FlagIcon className="h-4 w-4" />} onClick={props.onReport}>
              {t('messages.report')}
            </CtxItem>
          )}
          {/* Delete for me — hides it from just my view, so it isn't red. */}
          <CtxItem icon={<EyeOffIcon className="h-4 w-4" />} onClick={props.onDeleteForMe}>
            {t('messages.deleteForMe')}
          </CtxItem>
          {/* Delete for everyone — own messages or a moderator. */}
          {(props.mine || props.canModerate) && (
            <CtxItem icon={<TrashIcon className="h-4 w-4" />} onClick={props.onDelete} danger>
              {t('messages.deleteForEveryone')}
            </CtxItem>
          )}
        </div>
      </div>
  );
  if (sheet) return <Modal.Backdrop isOpen onOpenChange={(open) => { if (!open) props.onClose(); }}>
    <Modal.Container placement="bottom" size="full" scroll="inside">
      <Modal.Dialog className="studio-sheet-dialog" aria-label={t('messages.actions')}>
        <Modal.CloseTrigger />
        <Modal.Header><Modal.Heading>{t('messages.actions')}</Modal.Heading></Modal.Header>
        <Modal.Body>{body}</Modal.Body>
      </Modal.Dialog>
    </Modal.Container>
  </Modal.Backdrop>;
  return <Portal>{body}</Portal>;
}

function CtxDivider() {
  // Space, not a drawn line, separates groups (no decorative rules).
  return <div className="h-2" role="separator" />;
}

function CtxItem(props: {
  icon?: React.ReactNode;
  onClick: () => void | Promise<void>;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={() => void props.onClick()}
      className={
        'group/ctx flex w-full items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-left text-[13.5px] leading-5 tracking-[-0.01em] outline-none transition-colors duration-100 focus-visible:bg-white/8 ' +
        (props.danger ? 'text-danger hover:bg-danger/12' : 'text-ink hover:bg-white/[0.07]')
      }
    >
      {props.icon && (
        <span
          className={
            'shrink-0 transition-colors ' +
            (props.danger ? 'text-danger' : 'text-ink-muted group-hover/ctx:text-ink')
          }
        >
          {props.icon}
        </span>
      )}
      <span className="flex-1 truncate">{props.children}</span>
    </button>
  );
}
